// The question bank (teachers and admins): browse, write and edit questions, and upload many at once
// from the Excel template.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link, navigate, useRoute } from '../router.js';
import { rpc } from '../api.js';
import { useApp } from '../state.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Field, Loading, Notice, Section, useRpc, useToast } from '../ui/components.jsx';
import { Rich, firstLine } from '../ui/rich.jsx';
import { AnswerInput, LETTERS, QuestionReview, Scenario, TYPE_LABEL, marksLabel } from '../ui/question.jsx';
import { plural } from '../format.js';
import { SheetError, questionsFromWorkbook, readWorkbook } from '../xlsx.js';

const TEMPLATE = '/question-template.xlsx';
const DIFF = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

/* ---------------------------------------------------------------- the bank */
export function QuestionBank() {
  const { query } = useRoute();
  const [filter, setFilter] = useState({ subject: query.get('subject') || '', topic: '', type: '', search: '', archived: false });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      rpc('staff_questions', { p_filter: filter }).then((d) => { if (live) { setData(d); setError(null); } }).catch((e) => live && setError(e));
    }, filter.search ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [filter.subject, filter.topic, filter.type, filter.search, filter.archived]);
  const set = (k) => (e) => setFilter({ ...filter, [k]: e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value, ...(k === 'subject' ? { topic: '' } : {}) });
  const sub = filter.subject ? `?subject=${encodeURIComponent(filter.subject)}` : '';
  return (
    <Shell title="Question bank" backTo="/tests" backLabel="Tests"
      actions={<Link class="btn btn-gold btn-sm" href={`/questions/new${sub}`}><Icon name="plus" /><span>New question</span></Link>}>
      <p class="muted">Questions for every subject, to build tests from. Each teacher can use them all; you edit your own (admins can edit any).</p>
      <div class="row">
        <Link class="btn btn-line btn-sm" href={`/questions/upload${sub}`}><Icon name="upload" /><span>Upload from Excel</span></Link>
        <a class="btn btn-ghost btn-sm" href={TEMPLATE} download="Vidyavedh question template.xlsx"><Icon name="sheet" /><span>Download the Excel template</span></a>
      </div>
      <div class="filters-grid">
        <Field label="Subject">{(id) => (
          <select id={id} value={filter.subject} onChange={set('subject')}>
            <option value="">All subjects</option>
            {((data && data.subjects) || []).map((s) => <option value={s} key={s}>{s}</option>)}
          </select>
        )}</Field>
        <Field label="Topic">{(id) => (
          <select id={id} value={filter.topic} onChange={set('topic')}>
            <option value="">All topics</option>
            {((data && data.topics) || []).map((s) => <option value={s} key={s}>{s}</option>)}
          </select>
        )}</Field>
        <Field label="Type">{(id) => (
          <select id={id} value={filter.type} onChange={set('type')}>
            <option value="">All types</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option value={k} key={k}>{v}</option>)}
          </select>
        )}</Field>
        <Field label="Search">{(id) => <input id={id} type="search" value={filter.search} onInput={set('search')} placeholder="Words in the question or topic" />}</Field>
      </div>
      <label class="check"><input type="checkbox" checked={filter.archived} onChange={set('archived')} /><span>Show archived questions</span></label>
      {error ? <ErrorBox error={error} /> : null}
      {!data ? <Loading rows={4} /> : !data.questions.length ? (
        <Empty icon="bank" title={filter.archived ? 'No archived questions' : 'No questions here yet'}
          action={<Link class="btn btn-navy" href={`/questions/new${sub}`}><Icon name="plus" /><span>Write a question</span></Link>}>
          Write questions one at a time, or fill in the Excel template and upload it.
        </Empty>
      ) : (
        <>
          <p class="count-line">{plural(data.questions.length, 'question')}{data.questions.length >= 500 ? ' (the first 500; narrow it down to see the rest)' : ''}</p>
          <ul class="list q-list">
            {data.questions.map((q) => (
              <li key={q.id}>
                <Link class="q-row" href={`/questions/${q.id}`}>
                  <span class={`q-type t-${q.type}`}>{TYPE_LABEL[q.type]}</span>
                  <span class="q-row-text">
                    <strong>{firstLine(q.body)}</strong>
                    <small>{[q.subject, q.topic, marksLabel(q.marks), DIFF[q.difficulty], q.case ? `Case: ${q.case.title}` : null,
                      q.used_in ? `in ${plural(Number(q.used_in), 'test')}` : null, q.author].filter(Boolean).join(' · ')}</small>
                  </span>
                  <Icon name="next" />
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </Shell>
  );
}

/* ---------------------------------------------------------------- one question */
function emptyQuestion(subject, keep = {}) {
  return {
    subject: subject || '', topic: keep.topic || '', type: keep.type || 'mcq', body: '', marks: '', explanation: '', difficulty: '',
    options: ['', '', '', ''], correct: keep.type === 'mr' ? [] : null,
    number: { value: '', tolerance: '', prefix: '', suffix: '' },
    dd: { rows: [''], choices: ['', ''], answer: [null] },
    caseId: keep.caseId || '', newCase: { title: '', scenario: '' },
  };
}

function fromQuestion(q) {
  const f = emptyQuestion(q.subject);
  Object.assign(f, { id: q.id, topic: q.topic || '', type: q.type, body: q.body, marks: String(q.marks), explanation: q.explanation || '',
    difficulty: q.difficulty || '', caseId: q.case ? q.case.id : '' });
  if (q.type === 'mcq' || q.type === 'mr') { f.options = q.options; f.correct = q.answer; }
  if (q.type === 'number') f.number = { value: String(q.answer.value), tolerance: Number(q.answer.tolerance) ? String(q.answer.tolerance) : '', prefix: q.options.prefix || '', suffix: q.options.suffix || '' };
  if (q.type === 'dropdown') f.dd = { rows: q.options.rows, choices: q.options.choices, answer: q.answer };
  return f;
}

// The editor's state as the database expects it.
function toPayload(f) {
  const base = { id: f.id || null, subject: f.subject, topic: f.topic, type: f.type, body: f.body, marks: f.marks || null,
    explanation: f.explanation, difficulty: f.difficulty || null };
  if (f.caseId === 'new') base.case = { title: f.newCase.title, scenario: f.newCase.scenario };
  else if (f.caseId) base.case = { id: f.caseId };
  if (f.type === 'mcq') return { ...base, options: f.options, answer: f.correct };
  if (f.type === 'mr') return { ...base, options: f.options, answer: f.correct || [] };
  if (f.type === 'number') return { ...base, options: { prefix: f.number.prefix, suffix: f.number.suffix }, answer: { value: f.number.value, tolerance: f.number.tolerance } };
  if (f.type === 'dropdown') return { ...base, options: { rows: f.dd.rows, choices: f.dd.choices }, answer: f.dd.answer };
  return { ...base, options: [], answer: null };
}

// How students would see it (for the preview).
function previewQ(f, cases) {
  const p = toPayload(f);
  const c = f.caseId === 'new' ? { id: 'new', title: f.newCase.title, scenario: f.newCase.scenario } : cases.find((x) => x.id === f.caseId);
  const q = { ...p, marks: Number(f.marks) || (f.type === 'written' ? 10 : 2), case_id: c ? c.id : null, case_title: c ? c.title : '', scenario: c ? c.scenario : '' };
  if (f.type === 'mr') q.pick = (f.correct || []).length || undefined;
  if (f.type === 'number') q.answer = { value: f.number.value, tolerance: f.number.tolerance || 0 };
  return q;
}

export function QuestionEditor({ id }) {
  const { query } = useRoute();
  const { me } = useApp();
  const toast = useToast();
  const existing = useRpc('staff_get_question', { p_id: id }, [id], { skip: !id });
  const [f, setF] = useState(() => emptyQuestion(query.get('subject')));
  const [bank, setBank] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(false);
  useLayoutEffect(() => { if (existing.data) setF(fromQuestion(existing.data)); }, [existing.data]);
  useEffect(() => {
    let live = true;
    rpc('staff_questions', { p_filter: { subject: f.subject || null, meta_only: true } })
      .then((d) => { if (live) setBank(d); }).catch(() => {});
    return () => { live = false; };
  }, [f.subject]);
  useEffect(() => { if (!f.subject && bank && bank.subjects.length) setF((x) => ({ ...x, subject: x.subject || bank.subjects[0] })); }, [bank]);

  const q = existing.data;
  const mine = !q || me.role === 'admin' || q.created_by === me.id;
  const cases = (bank && bank.cases) || [];
  const set = (k) => (e) => setF({ ...f, [k]: e.currentTarget.value });
  const setType = (type) => setF({ ...f, type, correct: type === 'mr' ? [] : null });
  const setOption = (i, v) => setF({ ...f, options: f.options.map((o, j) => (j === i ? v : o)) });
  const removeOption = (i) => {
    const options = f.options.filter((_, j) => j !== i);
    let correct = f.correct;
    if (f.type === 'mcq') correct = correct === i ? null : correct > i ? correct - 1 : correct;
    else correct = (correct || []).filter((c) => c !== i).map((c) => (c > i ? c - 1 : c));
    setF({ ...f, options, correct });
  };
  const toggleCorrect = (i) => {
    if (f.type === 'mcq') setF({ ...f, correct: i });
    else { const s = new Set(f.correct || []); if (s.has(i)) s.delete(i); else s.add(i); setF({ ...f, correct: [...s].sort((a, b) => a - b) }); }
  };
  const setDd = (patch) => setF({ ...f, dd: { ...f.dd, ...patch } });

  const save = async (again) => {
    setBusy(again ? 'again' : 'save'); setErr(null);
    try {
      const saved = await rpc('staff_save_question', { p_question: toPayload(f) });
      toast(f.id ? 'Question saved' : 'Question added to the bank');
      if (again) { setF(emptyQuestion(saved.subject, { topic: saved.topic, type: saved.type, caseId: saved.case ? saved.case.id : '' })); setPreview(false); window.scrollTo(0, 0); }
      else navigate(`/questions?subject=${encodeURIComponent(saved.subject)}`);
    } catch (ex) { setErr(ex.message); }
    setBusy('');
  };
  const copy = async () => {
    setBusy('copy'); setErr(null);
    try {
      const c = await rpc('staff_save_question', { p_question: { ...toPayload(f), id: null } });
      toast('Copied: this one is yours to change');
      navigate(`/questions/${c.id}`, { replace: true });
    } catch (ex) { setErr(ex.message); }
    setBusy('');
  };
  const archive = async (yes) => {
    setBusy('archive');
    try { await rpc('staff_archive_question', { p_id: id, p_archived: yes }); toast(yes ? 'Archived: it no longer shows in the bank' : 'Back in the bank'); existing.reload(true); } catch (ex) { toast(ex.message, 'bad'); }
    setBusy('');
  };

  if (id && existing.loading && !q) return <Shell backTo="/questions" backLabel="Question bank"><Loading rows={4} /></Shell>;
  if (id && existing.error) return <Shell backTo="/questions" backLabel="Question bank"><ErrorBox error={existing.error} onRetry={existing.reload} /></Shell>;
  const pq = previewQ(f, cases);
  return (
    <Shell title={f.id ? 'Edit question' : 'New question'} backTo={`/questions${f.subject ? `?subject=${encodeURIComponent(f.subject)}` : ''}`} backLabel="Question bank">
      {q && !mine ? <Notice icon="info">Written by {q.author || 'another teacher'}. You can use it in your tests as it is, or copy it to make your own version.</Notice> : null}
      {q && q.used_in ? <Notice icon="info">It is in {plural(Number(q.used_in), 'test')}. Tests already published keep the version their students see.</Notice> : null}
      {q && q.archived ? <Notice tone="warn" icon="alert">Archived: it doesn’t show in the bank or in the question picker.</Notice> : null}
      <form class="form q-editor" onSubmit={(e) => { e.preventDefault(); save(false); }}>
        <fieldset disabled={!mine} class="q-editor-fields">
          <div class="row-2">
            <Field label="Subject">{(fid) => (
              <select id={fid} value={f.subject} onChange={(e) => setF({ ...f, subject: e.currentTarget.value, caseId: '' })} required>
                {!f.subject ? <option value="">Choose</option> : null}
                {((bank && bank.subjects) || (f.subject ? [f.subject] : [])).map((s) => <option value={s} key={s}>{s}</option>)}
              </select>
            )}</Field>
            <Field label="Topic">{(fid) => (
              <>
                <input id={fid} type="text" maxLength={120} list="topic-list" value={f.topic} onInput={set('topic')} placeholder="Group accounts" />
                <datalist id="topic-list">{((bank && bank.topics) || []).map((t) => <option value={t} key={t} />)}</datalist>
              </>
            )}</Field>
          </div>
          <div class="field" role="group" aria-labelledby="type-label">
            <span class="field-label" id="type-label">Type</span>
            <div class="seg">
              {Object.entries(TYPE_LABEL).map(([k, v]) => (
                <button type="button" key={k} class={`quick-btn${f.type === k ? ' is-on' : ''}`} aria-pressed={f.type === k ? 'true' : 'false'} onClick={() => setType(k)}>{v}</button>
              ))}
            </div>
          </div>
          <Field label="Case (optional)" hint="Questions in the same case share one scenario, like ACCA's OT cases.">{(fid) => (
            <select id={fid} value={f.caseId} onChange={set('caseId')}>
              <option value="">No case</option>
              {cases.map((c) => <option value={c.id} key={c.id}>{c.title}</option>)}
              <option value="new">New case…</option>
            </select>
          )}</Field>
          {f.caseId === 'new' ? (
            <div class="card case-new">
              <Field label="Case name">{(fid) => <input id={fid} type="text" required maxLength={200} value={f.newCase.title} onInput={(e) => setF({ ...f, newCase: { ...f.newCase, title: e.currentTarget.value } })} placeholder="Hill Co" />}</Field>
              <Field label="Scenario" hint="Students read it above each of the case's questions. Tables pasted from Excel keep their columns.">{(fid) => (
                <textarea id={fid} rows={6} maxLength={20000} value={f.newCase.scenario} onInput={(e) => setF({ ...f, newCase: { ...f.newCase, scenario: e.currentTarget.value } })} />
              )}</Field>
            </div>
          ) : null}
          <Field label="Question" hint="Paste a table from Excel and it keeps its columns. A blank line starts a new paragraph; start lines with - for a list.">{(fid) => (
            <textarea id={fid} rows={6} required maxLength={20000} value={f.body} onInput={set('body')} placeholder="Which of the following…" />
          )}</Field>

          {f.type === 'mcq' || f.type === 'mr' ? (
            <div class="field" role="group" aria-labelledby="opts-label">
              <span class="field-label" id="opts-label">Options <small>({f.type === 'mcq' ? 'tick the right one' : 'tick every right one'})</small></span>
              <ol class="opt-list">
                {f.options.map((o, i) => (
                  <li class="opt-row" key={i}>
                    <label class="opt-correct" title="Correct answer">
                      <input type={f.type === 'mcq' ? 'radio' : 'checkbox'} name="correct" checked={f.type === 'mcq' ? f.correct === i : (f.correct || []).includes(i)} onChange={() => toggleCorrect(i)} />
                      <span class="sr-only">Option {LETTERS[i]} is correct</span>
                    </label>
                    <span class="choice-letter" aria-hidden="true">{LETTERS[i]}</span>
                    <input type="text" aria-label={`Option ${LETTERS[i]}`} maxLength={2000} value={o} onInput={(e) => setOption(i, e.currentTarget.value)} />
                    <button type="button" class="icon-btn" disabled={f.options.length <= 2} onClick={() => removeOption(i)} aria-label={`Remove option ${LETTERS[i]}`}><Icon name="close" /></button>
                  </li>
                ))}
              </ol>
              {f.options.length < 8 ? <Button variant="line" size="sm" icon="plus" onClick={() => setF({ ...f, options: [...f.options, ''] })}>Add an option</Button> : null}
            </div>
          ) : null}

          {f.type === 'number' ? (
            <div class="row-2">
              <Field label="Correct answer">{(fid) => <input id={fid} type="text" inputMode="decimal" required value={f.number.value} onInput={(e) => setF({ ...f, number: { ...f.number, value: e.currentTarget.value } })} placeholder="1,250,000" />}</Field>
              <Field label="Allowed difference" hint="0 or empty: exactly right.">{(fid) => <input id={fid} type="text" inputMode="decimal" value={f.number.tolerance} onInput={(e) => setF({ ...f, number: { ...f.number, tolerance: e.currentTarget.value } })} placeholder="0" />}</Field>
              <Field label="Before the box (optional)">{(fid) => <input id={fid} type="text" maxLength={10} value={f.number.prefix} onInput={(e) => setF({ ...f, number: { ...f.number, prefix: e.currentTarget.value } })} placeholder="$" />}</Field>
              <Field label="After the box (optional)">{(fid) => <input id={fid} type="text" maxLength={10} value={f.number.suffix} onInput={(e) => setF({ ...f, number: { ...f.number, suffix: e.currentTarget.value } })} placeholder="%" />}</Field>
            </div>
          ) : null}

          {f.type === 'dropdown' ? (
            <>
              <div class="field" role="group" aria-labelledby="ch-label">
                <span class="field-label" id="ch-label">Choices in every drop-down list</span>
                <ol class="opt-list">
                  {f.dd.choices.map((c, i) => (
                    <li class="opt-row" key={i}>
                      <span class="choice-letter" aria-hidden="true">{LETTERS[i]}</span>
                      <input type="text" aria-label={`Choice ${LETTERS[i]}`} maxLength={200} value={c}
                        onInput={(e) => setDd({ choices: f.dd.choices.map((x, j) => (j === i ? e.currentTarget.value : x)) })} />
                      <button type="button" class="icon-btn" disabled={f.dd.choices.length <= 2} aria-label={`Remove choice ${LETTERS[i]}`}
                        onClick={() => setDd({ choices: f.dd.choices.filter((_, j) => j !== i), answer: f.dd.answer.map((a) => (a === i ? null : a > i ? a - 1 : a)) })}><Icon name="close" /></button>
                    </li>
                  ))}
                </ol>
                {f.dd.choices.length < 8 ? <Button variant="line" size="sm" icon="plus" onClick={() => setDd({ choices: [...f.dd.choices, ''] })}>Add a choice</Button> : null}
              </div>
              <div class="field" role="group" aria-labelledby="rows-label">
                <span class="field-label" id="rows-label">Statements, each with its correct choice</span>
                <ol class="dd-rows">
                  {f.dd.rows.map((r, i) => (
                    <li class="dd-edit" key={i}>
                      <input type="text" aria-label={`Statement ${i + 1}`} maxLength={2000} value={r}
                        onInput={(e) => setDd({ rows: f.dd.rows.map((x, j) => (j === i ? e.currentTarget.value : x)) })} />
                      <select aria-label={`Correct choice for statement ${i + 1}`} value={f.dd.answer[i] ?? ''}
                        onChange={(e) => setDd({ answer: f.dd.answer.map((x, j) => (j === i ? (e.currentTarget.value === '' ? null : Number(e.currentTarget.value)) : x)) })}>
                        <option value="">Correct choice…</option>
                        {f.dd.choices.map((c, k) => <option value={k} key={k}>{LETTERS[k]}: {c || '(empty)'}</option>)}
                      </select>
                      <button type="button" class="icon-btn" disabled={f.dd.rows.length <= 1} aria-label={`Remove statement ${i + 1}`}
                        onClick={() => setDd({ rows: f.dd.rows.filter((_, j) => j !== i), answer: f.dd.answer.filter((_, j) => j !== i) })}><Icon name="close" /></button>
                    </li>
                  ))}
                </ol>
                {f.dd.rows.length < 10 ? <Button variant="line" size="sm" icon="plus" onClick={() => setDd({ rows: [...f.dd.rows, ''], answer: [...f.dd.answer, null] })}>Add a statement</Button> : null}
              </div>
            </>
          ) : null}

          <Field label={f.type === 'written' ? 'Model answer and marking guide' : 'Explanation (shown after they answer)'}>{(fid) => (
            <textarea id={fid} rows={f.type === 'written' ? 8 : 4} maxLength={20000} value={f.explanation} onInput={set('explanation')} />
          )}</Field>
          <div class="row-2">
            <Field label="Marks" hint={f.type === 'written' ? 'Empty: 10.' : 'Empty: 2, all or nothing like ACCA.'}>{(fid) => <input id={fid} type="number" min={0.5} max={100} step={0.5} value={f.marks} onInput={set('marks')} placeholder={f.type === 'written' ? '10' : '2'} />}</Field>
            <Field label="Difficulty (optional)">{(fid) => (
              <select id={fid} value={f.difficulty} onChange={set('difficulty')}>
                <option value="">Not set</option><option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
              </select>
            )}</Field>
          </div>
        </fieldset>
        {err ? <p class="field-error" role="alert">{err}</p> : null}
        <div class="form-actions">
          {mine ? <Button type="submit" variant="navy" busy={busy === 'save'}>{f.id ? 'Save' : 'Add to the bank'}</Button> : null}
          {mine && !f.id ? <Button variant="line" busy={busy === 'again'} onClick={() => save(true)}>Add and write another</Button> : null}
          <Button variant="ghost" icon="eye" onClick={() => setPreview(!preview)} aria-expanded={preview ? 'true' : 'false'}>{preview ? 'Hide preview' : 'Preview'}</Button>
          {q ? <Button variant="ghost" icon="copy" busy={busy === 'copy'} onClick={copy}>Copy</Button> : null}
          {q && mine ? <Button variant="ghost" icon={q.archived ? 'refresh' : 'trash'} busy={busy === 'archive'} onClick={() => archive(!q.archived)}>{q.archived ? 'Restore' : 'Archive'}</Button> : null}
        </div>
      </form>
      {preview ? (
        <div class="q-preview-wrap"><Section title="Preview: as students see it" id="q-preview">
          <div class="q-card">
            <Scenario q={pq} />
            <Rich text={pq.body || 'The question appears here.'} class="q-body" />
            <AnswerInput q={pq} value={null} idBase="pv" onChange={() => {}} disabled />
          </div>
          <h3 class="preview-after">After they submit</h3>
          <QuestionReview item={{ id: 'pv', q: pq, response: null, score: null }} revealed number={1} preview />
        </Section></div>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- upload from Excel */
export function UploadQuestions() {
  const { query } = useRoute();
  const toast = useToast();
  const { data: bank } = useRpc('staff_questions', { p_filter: { meta_only: true } });
  const [subject, setSubject] = useState(query.get('subject') || '');
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [problem, setProblem] = useState(null);
  const [serverErrors, setServerErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (bank && bank.subjects.length) setSubject((s) => s || bank.subjects[0]); }, [bank]);

  const reading = useRef(0);
  const read = async (e) => {
    const f = e.currentTarget.files && e.currentTarget.files[0];
    const n = ++reading.current; // a file picked later wins, even if an earlier one finishes reading after it
    setRows(null); setProblem(null); setServerErrors([]); setFile(f || null);
    if (!f) return;
    try {
      const parsed = questionsFromWorkbook(readWorkbook(await f.arrayBuffer()));
      if (n === reading.current) setRows(parsed);
    } catch (ex) {
      if (n === reading.current) setProblem(ex instanceof SheetError ? ex.message : 'This file couldn’t be read. Save it as an Excel workbook (.xlsx) and try again.');
    }
  };
  const problems = useMemo(() => (rows || []).flatMap((r) => r.problems.map((m) => ({ row: r.row, message: m }))), [rows]);
  const upload = async () => {
    setBusy(true); setServerErrors([]);
    try {
      const res = await rpc('staff_import_questions', { p_subject: subject, p_rows: rows.map(({ problems: _p, ...r }) => r) });
      if (res.errors && res.errors.length) setServerErrors(res.errors);
      else { toast(`${plural(res.added, 'question')} added to the ${subject} bank`); navigate(`/questions?subject=${encodeURIComponent(subject)}`); }
    } catch (ex) { setProblem(ex.message); }
    setBusy(false);
  };
  const allProblems = [...problems, ...serverErrors];
  return (
    <Shell title="Upload questions" backTo={`/questions${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`} backLabel="Question bank">
      <ol class="steps">
        <li><a href={TEMPLATE} download="Vidyavedh question template.xlsx">Download the Excel template</a>. Its Examples sheet shows one of each type.</li>
        <li>Fill in the Questions sheet, one question per row (and the Cases sheet for any case scenarios). Save it.</li>
        <li>Choose the subject and the file below. Nothing is added until every row is right.</li>
      </ol>
      <div class="form">
        <div class="row-2">
          <Field label="Subject">{(id) => (
            <select id={id} value={subject} onChange={(e) => setSubject(e.currentTarget.value)}>
              {((bank && bank.subjects) || []).map((s) => <option value={s} key={s}>{s}</option>)}
            </select>
          )}</Field>
          <Field label="Excel file (.xlsx)">{(id) => <input id={id} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={read} />}</Field>
        </div>
      </div>
      {problem ? <Notice tone="bad" icon="alert">{problem}</Notice> : null}
      {rows ? (
        <>
          {allProblems.length ? (
            <div class="notice notice-bad" role="alert">
              <Icon name="alert" />
              <div>
                <p><strong>{plural(allProblems.length, 'thing')} to fix in {file ? file.name : 'the file'}</strong>, then upload it again:</p>
                <ul class="problem-list">{allProblems.slice(0, 50).map((p, i) => <li key={i}>Row {p.row}: {p.message}</li>)}</ul>
              </div>
            </div>
          ) : <Notice icon="done">{plural(rows.length, 'question')} ready to add to the {subject} bank.</Notice>}
          <div class="table-wrap" tabIndex={0} role="region" aria-label="Questions in the file">
            <table class="data-table">
              <thead><tr><th scope="col" class="num">Row</th><th scope="col">Type</th><th scope="col">Question</th><th scope="col">Topic</th><th scope="col">Case</th></tr></thead>
              <tbody>
                {rows.slice(0, 200).map((r) => (
                  <tr key={r.row} class={r.problems.length || serverErrors.some((x) => x.row === r.row) ? 'is-bad' : ''}>
                    <td class="num">{r.row}</td><td>{TYPE_LABEL[r.type] || r.type || '?'}</td><td>{firstLine(r.body, 90)}</td><td>{r.topic}</td><td>{r.case_title || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div class="form-actions">
            <Button variant="navy" icon="upload" busy={busy} disabled={Boolean(problems.length) || !subject} onClick={upload}>Add {plural(rows.length, 'question')}</Button>
          </div>
        </>
      ) : null}
    </Shell>
  );
}
